import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; fifth: string; stab: readonly string[]; pad: readonly string[]; arp: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Am: { root: 'A1', fifth: 'E2', stab: ['A3', 'C4', 'E4'], pad: ['A2', 'E3', 'A3', 'C4'], arp: ['A3', 'C4', 'E4', 'A4'] },
  Dm: { root: 'D2', fifth: 'A1', stab: ['A3', 'D4', 'F4'], pad: ['D3', 'A3', 'D4', 'F4'], arp: ['A3', 'D4', 'F4', 'A4'] },
  E7: { root: 'E2', fifth: 'B1', stab: ['G#3', 'B3', 'D4'], pad: ['E3', 'G#3', 'B3', 'D4'], arp: ['G#3', 'B3', 'D4', 'E4'] },
  F: { root: 'F1', fifth: 'C2', stab: ['A3', 'C4', 'F4'], pad: ['F2', 'C3', 'F3', 'A3'], arp: ['A3', 'C4', 'F4', 'A4'] },
  Gd: { root: 'G#1', fifth: 'D2', stab: ['G#3', 'B3', 'F4'], pad: ['G#2', 'D3', 'F3', 'B3'], arp: ['G#3', 'B3', 'D4', 'F4'] },
  C: { root: 'C2', fifth: 'G1', stab: ['G3', 'C4', 'E4'], pad: ['C3', 'G3', 'C4', 'E4'], arp: ['G3', 'C4', 'E4', 'G4'] },
  Bb: { root: 'A#1', fifth: 'F1', stab: ['A#3', 'D4', 'F4'], pad: ['A#2', 'F3', 'A#3', 'D4'], arp: ['A#3', 'D4', 'F4', 'A#4'] },
};

const PROGS: readonly (readonly string[])[] = [
  ['Am', 'Dm', 'E7', 'Am'],
  ['Am', 'F', 'E7', 'E7'],
  ['Am', 'Gd', 'Am', 'E7'],
  ['Dm', 'Am', 'Bb', 'E7'],
  ['Am', 'C', 'Dm', 'E7'],
];

const HOOK: Note[][] = [
  [[0, 'E5', 4], [4, 'A5', 4], [8, 'G#5', 2], [10, 'A5', 2], [12, 'E5', 4]],
  [[0, 'F5', 4], [4, 'D5', 2], [6, 'F5', 2], [8, 'A5', 6], [14, 'G#5', 2]],
  [[0, 'G#5', 2], [2, 'B5', 2], [4, 'D5', 4], [8, 'E5', 2], [10, 'F5', 2], [12, 'G#5', 4]],
  [[0, 'A5', 6], [8, 'E5', 2], [10, 'C5', 2], [12, 'A4', 4]],
];

const HARMONIC = pcs(['A4', 'B4', 'C5', 'D5', 'E5', 'F5', 'G#5']);
const KEYS = [0, 2, -2, 3, -3, 1, -1];
const PIZZ: readonly (readonly number[])[] = [
  [0, 2, 1, 2, 0, 3, 1, 2],
  [0, 1, 2, 1, 3, 1, 2, 1],
  [0, 2, 3, 2, 1, 2, 3, 2],
];
const BOUNCE = 'X.....x.X.......';
const FOUR = 'X...X...X...X...';
const ONE = 'X.......x.......';
const HATS = ['..x...x...x...x.', '..x-..x-..x-..x-', '-.x-x.x--.x-x.x-'];

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 3 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 2 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(SPOOKY, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const pz = ch.pick(PIZZ);
  const hatP = ch.pick(HATS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([...c.pad, ...c.stab]) }]),
        HARMONIC,
        [64, 84],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 54, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const rng = createRng(`spooky-${seed}`);
  const human = (v: number) => Math.min(1, v * (0.94 + rng.float() * 0.12));

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const glide = (bar: number, step: number, from: string, to: string, steps: number, vel: number) =>
    events.push({ v: 'lead', t: g.at(bar, step), bar, step, vel, dur: steps * s, note: tr(from), glide: { to: tr(to), at: 0, time: steps * s * 0.9 } });
  const pizz = (bar: number, arp: readonly string[], vel: number, upTo = 16) => {
    for (let k = 0; k < 8 && k * 2 < upTo; k++) play('pizz', bar, k * 2, arp[pz[k]!]!, s * 1.4, human(vel * (k % 2 === 0 ? 1 : 0.78)));
  };
  const tiptoe = (bar: number, arp: readonly string[], vel: number) => {
    for (const [k, st] of [0, 4, 8, 12].entries()) play('pizz', bar, st, arp[pz[k * 2]!]!, s * 1.2, human(vel));
  };
  const oompah = (bar: number, c: Chord, next: Chord, vel: number, bounce: boolean, stop: boolean) => {
    play('bass', bar, 0, tr(c.root), s * (stop ? 7 : 3.4), human(vel));
    if (stop) return;
    if (bounce) play('bass', bar, 6, tr(noteName(midi(c.root) + 12)), s * 1.4, human(vel * 0.62));
    play('bass', bar, 8, tr(c.fifth), s * 3.4, human(vel * 0.9));
    if (bounce && next.root !== c.root) play('bass', bar, 14, tr(noteName(midi(next.root) - 1)), s * 1.6, human(vel * 0.7));
  };
  const stabs = (bar: number, stab: readonly string[], vel: number, stop: boolean) => {
    chord('organ', bar, 4, stab, s * 1.5, human(vel));
    if (!stop) chord('organ', bar, 12, stab, s * 1.5, human(vel * 0.92));
  };
  const melody = (v: string, bar: number, notes: readonly Note[], up: number, vel: number, short: boolean, cut = 16) => {
    for (const [step, note, len, nv] of notes) {
      if (step >= cut) continue;
      play(v, bar, step, tr(noteName(midi(note) + up)), (short ? Math.min(len, 2) : len) * s * 0.96, human((nv ?? 0.9) * vel));
    }
  };

  const autos: Score['autos'] = [{ v: 'padLp', points: [[g.at(0), 600], [g.at(intro), 2200]] }];
  if (brk !== undefined) {
    const n = sizeOf(form, 'break');
    autos.push({ v: 'padLp', points: [[g.at(brk), 1300], [g.at(brk + n - 1), 3200], [g.at(brk + n), 2200]] });
    autos.push({ v: 'pizzLp', points: [[g.at(brk), 1400], [g.at(brk + n - 1), 4800], [g.at(brk + n), 3600]] });
  }
  autos.push({ v: 'padLp', points: [[g.at(total - 2), 2200], [g.at(total), 500]] });

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const next = prog[cycle(i + 1, 4)]!;
    const pad = c.pad.map(tr);
    const stab = c.stab.map(tr);
    const arp = c.arp.map(tr);
    const drop = b.name === 'drop' || b.name === 'drop2';

    if (end) {
      chord('pad', b.bar, 0, pad, g.barLength * 1.5, 0.6);
      chord('organ', b.bar, 0, stab, s * 3, 0.85);
      play('bass', b.bar, 0, tr(c.root), s * 6, 0.9);
      hit('kick', b.bar, 0, 0.9);
      hit('bell', b.bar, 0, 0.7);
      glide(b.bar, 4, 'A4', 'A5', 8, 0.5);
      hit('rattle', b.bar, 12, 0.7);
      play('pizz', b.bar, 14, arp[0]!, s * 1.2, 0.55);
      continue;
    }

    if (b.name === 'intro') {
      chord('pad', b.bar, 0, pad, g.barLength * 1.02, 0.7);
      if (b.index === 0) hit('bell', b.bar, 0, 0.75);
      if (b.index < b.bars / 2) {
        tiptoe(b.bar, arp, 0.5);
        if (b.index === b.bars / 2 - 1) hit('rattle', b.bar, 14, 0.55);
        continue;
      }
      hit('kick', b.bar, 0, 0.7);
      hit('kick', b.bar, 8, 0.62);
      pizz(b.bar, arp, 0.55);
      oompah(b.bar, c, next, 0.55, false, false);
      stabs(b.bar, stab, 0.55, false);
      continue;
    }

    if (b.name === 'break') {
      const last = b.index === b.bars - 1;
      const build = b.bars >= 3 && b.index === b.bars - 2;
      if (last) {
        glide(b.bar, 0, 'A5', 'A3', 6, 0.6);
        play('pizz', b.bar, 8, arp[0]!, s * 1.2, 0.5);
        play('pizz', b.bar, 10, arp[2]!, s * 1.2, 0.5);
        chord('organ', b.bar, 12, [stab[0]!, noteName(midi(stab[0]!) + 1), stab[2]!], s * 2, 0.75);
        hit('rattle', b.bar, 14, 0.75);
        continue;
      }
      chord('pad', b.bar, 0, pad, g.barLength * 1.02, 0.75);
      tiptoe(b.bar, arp, 0.5);
      play('bass', b.bar, 0, tr(c.root), g.barLength * 0.7, 0.7);
      if (build) {
        for (const h of hits(FOUR)) hit('kick', b.bar, h.step, 0.85);
        for (let st = 0; st < 16; st += st < 8 ? 2 : 1) hit('clap', b.bar, st, 0.35 + (st / 15) * 0.55);
      } else if (b.index === 0) hit('rattle', b.bar, 14, 0.6);
      continue;
    }

    const outroHalf = b.name === 'outro' && b.index >= b.bars / 2;
    const stop = drop && i === 3;
    const preDrop = b.bar === drop1 - 1;
    chord('pad', b.bar, 0, pad, g.barLength * 1.02, drop ? 0.5 : 0.6);
    for (const h of hits(drop ? FOUR : outroHalf ? ONE : BOUNCE)) if (!stop || h.step < 8) hit('kick', b.bar, h.step, human(h.vel));
    if (!outroHalf) hit('clap', b.bar, 4, human(0.9));
    if (!stop && !preDrop) hit('clap', b.bar, 12, human(outroHalf ? 0.7 : 0.9));
    if (preDrop) for (let st = 8; st < 16; st++) hit('clap', b.bar, st, 0.4 + ((st - 8) / 7) * 0.5);
    if (!stop) for (const h of hits(drop ? hatP : HATS[0]!)) hit('hat', b.bar, h.step, human(h.vel * (drop ? 0.7 : 0.5)));
    stabs(b.bar, stab, drop ? 0.8 : 0.7, stop);
    oompah(b.bar, c, next, drop ? 0.95 : 0.85, !outroHalf, stop);
    pizz(b.bar, arp, drop ? 0.68 : outroHalf ? 0.5 : 0.6, stop ? 8 : 16);
    if (i === 1 && !stop) hit('rattle', b.bar, 14, 0.6);
    if (stop) {
      const top = midi(arp[3]!) + 12;
      for (let k = 0; k < 8; k++) play('xylo', b.bar, 8 + k, noteName(top - k), s * 1.2, 0.55 + k * 0.05);
      hit('rattle', b.bar, 15, 0.7);
    }
    const h = hook[i]!;
    const cut = stop ? 8 : 16;
    if (b.name === 'drop') melody('xylo', b.bar, h, 12, 0.75, true, cut);
    if (b.name === 'drop2') {
      melody('lead', b.bar, h, 0, 0.8, false, cut);
      if (e >= 0.8) melody('xylo', b.bar, h, 12, 0.5, true, cut);
    }
    if (b.name === 'outro' && !outroHalf) melody('lead', b.bar, h, 0, 0.6, false);
    if (b.name === 'A' && e >= 0.6 && i === 3) melody('xylo', b.bar, h, 12, 0.55, true, 8);
  }

  if (brk !== undefined && drop2 !== undefined) {
    const n = sizeOf(form, 'break');
    for (let k = 0; k < Math.min(2, n - 2); k++) melody('lead', brk + k, hook[cycle(brk + k - intro, 4)]!, 0, 0.6, false);
    events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 1), bar: brk, step: 0, vel: 1 });
    const from = brk + Math.max(0, n - 2);
    events.push({ v: 'rise', t: g.at(from), end: g.at(brk + n - 1) - s, bar: from, step: 0, vel: 1 });
  }
  const riseFrom = Math.max(intro, drop1 - 2);
  events.push({ v: 'rise', t: g.at(riseFrom), end: g.at(drop1) - s * 2, bar: riseFrom, step: 0, vel: 1 });
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('impact', bar, 0, 1);
    hit('bell', bar, 0, 0.6);
  }

  return {
    id: trackId(SPOOKY, seed, p),
    template: SPOOKY.id,
    seed,
    params: p,
    title: ch.orig ? SPOOKY.ref.title : titleOf(seed),
    genre: SPOOKY.genre,
    bpm: p.bpm,
    swing: 54,
    lufs: -11,
    key,
    form,
    mix: { reverbDecay: 2.6, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.25, drive: 1.15, softness: 1.2, masterLowpass: 12000, pumpDepth: 0.1, pumpRelease: 0.2 },
    events,
    autos,
    accents: { kick: 'kick', clap: 'snare', organ: 'stab', pizz: 'melody', xylo: 'melody', lead: 'melody', impact: 'drop' },
  };
}

export const SPOOKY: Template = {
  id: 'spooky',
  genre: 'spooky-playful halloween',
  bpm: [118, 130],
  ref: { seed: 'horta', title: 'Horta', params: { bpm: 120, bars: 28, energy: 0.55 } },
  score,
};
